import { useEffect, useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import MobileNav from "./MobileNav";
import ContactDialog from "./ContactDialog";
import { NAV_ITEMS } from "@/constants/navItems";

const Header = ({ className }: { className?: string }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const rafId = useRef<number | null>(null);
  const lastScrollY = useRef<number>(0);

  const handleScroll = useCallback(() => {
    if (rafId.current !== null) return;
    rafId.current = requestAnimationFrame(() => {
      const currentScrollY = window.scrollY;
      if ((currentScrollY > 50) !== (lastScrollY.current > 50)) {
        setIsScrolled(currentScrollY > 50);
      }
      lastScrollY.current = currentScrollY;
      rafId.current = null;
    });
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId.current !== null) cancelAnimationFrame(rafId.current);
    };
  }, [handleScroll]);

  const scrollToSection = (hash: string) => {
    const el = document.getElementById(hash.replace("#", ""));
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-700 ease-out-smooth ${className || ""}`}
    >
      <div
        className={`mx-auto relative transition-all duration-700 ease-out-smooth ${
          isScrolled
            ? "max-w-3xl mt-3 rounded-full px-4 py-2"
            : "max-w-[80rem] px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12 2xl:px-16 py-4"
        }`}
      >
        {/* Pill background — fades in/out independently */}
        <div
          className={`absolute inset-0 rounded-full bg-white/70 backdrop-blur-2xl border border-white/80 shadow-[0_8px_20px_rgba(0,0,0,0.08)] transition-opacity duration-700 ease-out-smooth pointer-events-none ${
            isScrolled ? "opacity-100" : "opacity-0"
          }`}
        />
        <div className="flex items-center justify-between h-full relative z-10">
          {/* Logo */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center font-heading text-lg font-bold text-text-primary tracking-tight"
          >
            Joel Bruce<span className="text-primary">.</span>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.path}
                onClick={() => scrollToSection(item.path)}
                className="text-sm font-medium px-4 py-2 rounded-full text-text-secondary hover:bg-white/60 hover:text-text-primary transition-all duration-200"
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* CTA + Mobile */}
          <div className="flex items-center gap-3">
            <ContactDialog>
              <Button className="hidden md:flex bg-primary hover:bg-primary-hover text-primary-foreground font-semibold rounded-full px-6 py-2 text-sm shadow-none">
                Let's Talk
              </Button>
            </ContactDialog>
            <MobileNav />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
